import { blue, gold, green, grey, purple, red } from '@ant-design/colors';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
	getReadableLooseOrderSubtype,
	getReadablePriceType,
	OrderType,
	PaymentType,
	PriceType,
	TaskType,
	TaskView,
	TaskViewClassic,
	TaskViewLoose,
} from 'types';
import { Formatting } from '../../utils';
import { DriverCardEvent, DriverCardEventType } from './use-driver-card';

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	padding: 12px;
	border-radius: 6px;
	background: ${({ color }) => color};
	color: white;
	margin-bottom: 4px;

	> div {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	h4 {
		font-size: 18px;
		margin: 0;
		margin-bottom: 4px;
		color: white;
	}

	h5 {
		font-size: 18px;
		font-weight: 400;
		margin: 0;
		color: white;
	}

	:hover {
		cursor: pointer;
	}
`;

const getColor = (task: TaskView) => {
	if (task.orderType === OrderType.classic && task.order.onPlace) {
		return purple[5];
	}

	if (task.type === TaskType.delivery) {
		return green[5];
	}

	if (task.type === TaskType.claiming) {
		return red[5];
	}

	if (task.type === TaskType.replacement) {
		return gold[5];
	}

	return grey[5];
};

interface Props {
	event: DriverCardEvent;
}

export const DriverCardEventTile: React.FC<Props> = ({ event }) => {
	if (event.type === DriverCardEventType.classicOrder) {
		return <ClassicOrderEventTile task={event.task} />;
	}

	if (event.type === DriverCardEventType.looseOrder) {
		return <LooseOrderEventTile task={event.task} />;
	}

	if (event.type === DriverCardEventType.expense) {
		return <ExpenseEventTile expense={event.expense} />;
	}

	if (event.type === DriverCardEventType.settlement) {
		return <SettlementEventTile settlement={event.settlement} />;
	}

	return <div></div>;
};

const ClassicOrderEventTile: React.FC<{ task: TaskViewClassic }> = ({ task }) => {
	const navigate = useNavigate();

	const price = task.order.priceType === PriceType.gross ? task.order.grossPrice : task.order.netPrice;

	return (
		<Wrapper color={getColor(task)} onClick={() => navigate(`/panel/orders/${task.orderId}`)}>
			<div>
				<h4>
					{moment(task.takenAt).format('HH:mm')} - {moment(task.doneAt).format('HH:mm')}
				</h4>
				<h5>{task.order.address}</h5>
			</div>
			<div>
				<span>
					Płatność:{' '}
					{task.order.paymentType === PaymentType.cash
						? `Gotówka - ${task.order.paymentDate === task.doneAt ? 'pobrana' : 'niepobrana'}`
						: 'Przelew'}
				</span>
				<span>
					Kwota: {Formatting.money(price)} {getReadablePriceType(task.order.priceType)}
				</span>
				<span>Zaliczka: {task.order.advance ? Formatting.money(task.order.advance) : 'Brak'}</span>
			</div>
		</Wrapper>
	);
};

const LooseOrderEventTile: React.FC<{ task: TaskViewLoose }> = ({ task }) => {
	const navigate = useNavigate();

	const price = task.order.priceType === PriceType.gross ? task.order.grossPrice : task.order.netPrice;
	const payment = task.order.payments.find((p) => p.date === task.doneAt);

	return (
		<Wrapper color={getColor(task)} onClick={() => navigate(`/panel/orders/${task.orderId}/loose`)}>
			<div>
				<h4>
					{moment(task.takenAt).format('HH:mm')} - {moment(task.doneAt).format('HH:mm')} |{' '}
					{getReadableLooseOrderSubtype(task.order.subtype)}
				</h4>
				<h5>{task.order.address}</h5>
			</div>
			<div>
				<span>
					Płatność:{' '}
					{task.order.wantedPaymentType === PaymentType.cash
						? `Gotówka - ${payment ? 'pobrana' : 'niepobrana'}`
						: 'Przelew'}
				</span>
				<span>
					Kwota: {Formatting.money(price)} {getReadablePriceType(task.order.priceType)}
				</span>
				<span>Ilość: {task.order.count}</span>
			</div>
		</Wrapper>
	);
};

const ExpenseEventTile: React.FC<{ expense: any }> = ({ expense }) => {
	return (
		<Wrapper color={blue[5]}>
			<div>
				<h4>{moment(expense.created).format('HH:mm')}</h4>
				<h5>
					{expense.type.name} - {expense.name}
				</h5>
			</div>
			<div>
				<span>
					Kwota: {Formatting.money(expense.value)} {expense.value_type ? 'Brutto' : 'Netto'}
				</span>
				<span>Podatek: {expense.tax}%</span>
				<span>Przebieg: {expense.mileage ? Formatting.insertSpaces(expense.mileage) : '-'}</span>
			</div>
		</Wrapper>
	);
};

const SettlementEventTile: React.FC<{ settlement: any }> = ({ settlement }) => {
	return (
		<Wrapper color={purple[5]}>
			<div>
				<h4>{moment(settlement.created).format('HH:mm')}</h4>
				<h5>{Formatting.money(settlement.value)}</h5>
			</div>
			<div>
				<span>
					Odebrał: {settlement.created_by.name} {settlement.created_by.surname}
				</span>
			</div>
		</Wrapper>
	);
};
